import { BookACallDialog } from '@components/BookaCallDialog';
import GoogleSignIn from '@components/GoogleSignIn';
import PageContainer from '@components/PageContainer';
import { useLogin } from '@hooks/useLogin';
import useSession from '@hooks/useSession';
import AuthFormLayout from '@layouts/form/AuthFormLayout';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { useUserAnalytics } from '../../hooks/useUserAnalytics';
import { AUTH_EVENTS } from '../../utils/eventSchema';

// Identify user for analytics

function SignIn() {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { setIsAuthenticated } = useSession();
  const [isChecked, setIsChecked] = useState(!!sessionStorage.getItem('username'));
  const [openBookCall, setOpenBookCall] = useState(false);
  const { trackEvent } = useUserAnalytics();
  const navigate = useNavigate();
  const { mutate: loginMutation, isPending } = useLogin();

  const handleClickShowPassword = useCallback(() => {
    setPasswordVisible((prev) => !prev);
  }, []);

  const handleMouseDownPassword = useCallback((event) => {
    event.preventDefault();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      username: sessionStorage.getItem('username') ?? '',
      password: sessionStorage.getItem('password') ?? '',
    },
  });

  // Watch the values of username and password fields
  const username = watch('username');
  const password = watch('password');

  const onSubmit = (credentials) => {
    if (isChecked) {
      sessionStorage.setItem('username', credentials.username);
      sessionStorage.setItem('password', credentials.password);
    } else {
      sessionStorage.removeItem('username');
      sessionStorage.removeItem('password');
    }

    // Track login started
    trackEvent('auth', {
      ...AUTH_EVENTS.LOGIN_STARTED,
      email: credentials.username,
      attempt_time: new Date().toISOString(),
      device: navigator.userAgent,
    });

    loginMutation(credentials, {
      onSuccess: () => {
        setIsAuthenticated(true);
        navigate('/');
      },
    });
  };

  return (
    <>
      <PageContainer title="Qualz.ai | SignIn">
        <AuthFormLayout
          title="Good to see you again!👋"
          text="Let’s get back to your research! Log in and pick up where you left off."
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormControl fullWidth>
              <FormLabel htmlFor="username">Email *</FormLabel>
              <TextField
                fullWidth
                type="email"
                data-testid="email-input"
                placeholder="name@gmail.com"
                variant="outlined"
                id="username"
                {...register('username', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email',
                  },
                })}
                error={!!errors.username}
                helperText={errors.username ? errors.username.message : ''}
                sx={{ mb: 2 }}
              />
            </FormControl>
            <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Grid item>
                <FormLabel htmlFor="username">Password *</FormLabel>
              </Grid>
              <Grid item>
                <Typography
                  variant="body2"
                  data-testid="forgot-password"
                  sx={{ cursor: 'pointer', color: 'primary.main' }}
                  onClick={() => navigate('/forget-password')}
                >
                  Forgot password?
                </Typography>
              </Grid>
            </Grid>
            <TextField
              fullWidth
              placeholder="Enter Password"
              variant="outlined"
              data-testid="password-input"
              type={passwordVisible ? 'text' : 'password'}
              {...register('password', {
                required: 'Password is required',
              })}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <Button
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    variant="inherit"
                    sx={{ minWidth: '30px', px: 0 }}
                  >
                    {passwordVisible ? <VisibilityOff /> : <Visibility />}
                  </Button>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                    color="primary"
                  />
                }
                label="Remember Me"
                sx={{ mb: 2 }}
              />
            </Box>

            <Button
              variant="contained"
              fullWidth
              type="submit"
              disabled={!username || !password || isPending}
              sx={{
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: '500',
                py: 1.5,
                mt: 2,
              }}
            >
              {loginMutation.isPending ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>

            <Divider>or</Divider>

            {/* Google Sign-In Button */}
            <Box sx={{ mt: 2, mb: 4 }}>
              <GoogleSignIn fullWidth />
            </Box>

            <Typography variant="body1" align="center">
              Don&apos;t have an account?{' '}
              <Link to="/signup" underline="hover">
                Sign up
              </Link>
            </Typography>
          </form>
        </AuthFormLayout>
      </PageContainer>
      <BookACallDialog open={openBookCall} setOpen={setOpenBookCall} />
    </>
  );
}

export default SignIn;
